import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { common, economic, reduce } from "../../../../../i18n";
import { EcoEfficiencyDashboardContext } from "../../../../../contexts/EcoEfficiencyDashboardContext";
import {
  EcoEfficiencyDashboardMonthlyResults,
  EcoEfficiencyDashboardYearlyResults,
  EconomicComponentYearly,
} from "../../../../../models/dto/response/ResponseEcoEfficiencyV2";
import { MakeOptional } from "@mui/x-charts/models/helpers";
import { BarSeriesType } from "@mui/x-charts";
import CustomEcoEfficiencyChart from "../economicEfficiency/CustomEcoEfficiencyChart";
import { GridColDef, GridColumnGroupingModel } from "@mui/x-data-grid";
import { EcoEfficiencyPerformanceOverTimeDataGrid } from "./ecoEfficiencyPerformanceOverTime.type";
import { MenuData } from "../../../../../components/input/CustomSelect";
import NumberFormatterUtils from "../../../../../utils/numberFormatterUtils";
import { Box, Tooltip } from "@mui/material";
import { EconomicIndicatorComponent } from "../../../../../models/dto/response/ResponseCreateEconomicIndicators";

const EcoEfficiencyPerformanceOverTime = () => {
  const { t, i18n } = useTranslation([common, reduce, economic], {
    nsMode: "fallback",
  });
  const { indicators, yearly, monthly, selectedEcoEfficiency } =
    EcoEfficiencyDashboardContext();
  const [series, setSeries] = useState<MakeOptional<BarSeriesType, "type">[]>(
    [],
  );
  const [seriesMonth, setSeriesMonth] = useState<
    MakeOptional<BarSeriesType, "type">[]
  >([]);
  const [columnGroup, setColumnGroup] = useState<
    GridColumnGroupingModel | undefined
  >(undefined);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [columnsMonth, setColumnsMonth] = useState<GridColDef[]>([]);
  const [rows, setRows] = useState<EcoEfficiencyPerformanceOverTimeDataGrid[]>(
    [],
  );
  const [rowsMonth, setRowsMonth] = useState<
    EcoEfficiencyPerformanceOverTimeDataGrid[]
  >([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [labelsMonth, setLabelsMonth] = useState<string[]>([]);
  const [data, setData] = useState<EcoEfficiencyDashboardYearlyResults>();
  const [dataMonth, setDataMonth] =
    useState<EcoEfficiencyDashboardMonthlyResults>();
  const [filterOption, setFilterOption] = useState<MenuData[]>([]);
  const [selectedComponants, setSelectedComponants] =
    useState<string>("yearly");

  const createDefaultYearlyColumns = (): GridColDef[] => {
    return [
      {
        field: "id",
        headerName: t("No."),
        width: 80,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => params.row._id + 1,
      },
      {
        field: "year",
        headerName: t("Year"),
        minWidth: 120,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => params.row.id,
      },
      // {
      //   field: "economicValue",
      //   headerName: t("Economic-Value"),
      //   minWidth: 150,
      //   headerAlign: "center",
      //   align: "right",
      //   sortable: false,
      //   filterable: false,
      //   renderCell: (params) =>
      //     NumberFormatterUtils.numberFormat(params.row.economicValue) ?? 0,
      // },
      // {
      //   field: "environmentImpactValue",
      //   headerName: t("Environment-Impact-Value"),
      //   minWidth: 150,
      //   headerAlign: "center",
      //   align: "right",
      //   sortable: false,
      //   filterable: false,
      //   renderCell: (params) =>
      //     NumberFormatterUtils.numberFormat(
      //       params.row.environmentalImactValue
      //     ) ?? 0,
      // },
      {
        field: "normalizationBaseYear",
        headerName: t("Base-Year"),
        headerAlign: "center",
        align: "right",
        minWidth: 120,
        sortable: false,
        filterable: false,
        valueFormatter: (value) =>
          NumberFormatterUtils.numberFormat(value) ?? 0,
      },
      // {
      //   field: "normalizationPlan",
      //   headerName: t("Plan"),
      //   headerAlign: "center",
      //   align: "right",
      //   minWidth: 120,
      //   sortable: false,
      //   filterable: false,
      //   valueFormatter: (value) =>
      //     NumberFormatterUtils.numberFormat(value) ?? 0,
      // },
      {
        field: "normalizationActual",
        headerName: t("Actual"),
        headerAlign: "center",
        align: "right",
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Tooltip title={params.formattedValue}>
              <Box color={"var( --status-waiting-leased-in-text)"}>
                {NumberFormatterUtils.numberFormat(params.formattedValue)}
              </Box>
            </Tooltip>
          );
        },
      },
    ];
  };

  // ฟังก์ชันสำหรับสร้าง default columns สำหรับ monthly view
  const createDefaultMonthlyColumns = (): GridColDef[] => {
    return [
      {
        field: "id",
        headerName: t("No."),
        width: 80,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (params.row.month === t("Total")) {
            return "";
          }
          return params.row._id + 1;
        },
      },
      {
        field: "month",
        headerName: t("Month"),
        minWidth: 120,
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => params.row.month,
      },
      // {
      //   field: "economicValue",
      //   headerName: t("Economic-Value"),
      //   minWidth: 150,
      //   headerAlign: "center",
      //   align: "right",
      //   sortable: false,
      //   filterable: false,
      //   renderCell: (params) =>
      //     NumberFormatterUtils.numberFormat(params.row.economicValue) ?? 0,
      // },
      // {
      //   field: "environmentImpactValue",
      //   headerName: t("Environment-Impact-Value"),
      //   minWidth: 150,
      //   headerAlign: "center",
      //   align: "right",
      //   sortable: false,
      //   filterable: false,
      //   renderCell: (params) =>
      //     NumberFormatterUtils.numberFormat(
      //       params.row.environmentalImactValue
      //     ) ?? 0,
      // },
      {
        field: "normalizationBaseYear",
        headerName: t("Base-Year"),
        headerAlign: "center",
        align: "right",
        minWidth: 120,
        sortable: false,
        filterable: false,
        valueFormatter: (value) =>
          NumberFormatterUtils.numberFormat(value) ?? 0,
      },
      // {
      //   field: "normalizationPlan",
      //   headerName: t("Plan"),
      //   headerAlign: "center",
      //   align: "right",
      //   minWidth: 120,
      //   sortable: false,
      //   filterable: false,
      //   valueFormatter: (value) =>
      //     NumberFormatterUtils.numberFormat(value) ?? 0,
      // },
      {
        field: "normalizationActual",
        headerName: t("Actual"),
        headerAlign: "center",
        align: "right",
        minWidth: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Tooltip title={params.formattedValue}>
              <Box color={"var( --status-waiting-leased-in-text)"}>
                {NumberFormatterUtils.numberFormat(params.formattedValue)}
              </Box>
            </Tooltip>
          );
        },
      },
    ];
  };

  // ฟังก์ชันสำหรับสร้าง default column grouping
  const createDefaultColumnGrouping = (): GridColumnGroupingModel => {
    return [
      {
        groupId: t("Normalized-Eco-Efficiency"),
        children: [
          { field: `normalizationBaseYear` },
          { field: `normalizationPlan` },
          { field: `normalizationActual` },
        ],
        headerAlign: "center",
      },
    ];
  };

  // Helper function to format and convert back to number
  const formatToNumber = (value: number | null | undefined): number => {
    if (value == null) return 0;
    const formatted = NumberFormatterUtils.numberFormat(value, 4);
    return parseFloat(formatted?.replace(/,/g, "") || "0");
  };

  // Initial default columns เมื่อ component mount
  useEffect(() => {
    // ตั้งค่า default columns ทันทีเมื่อ component mount
    const defaultYearlyColumns = createDefaultYearlyColumns();
    const defaultMonthlyColumns = createDefaultMonthlyColumns();
    const defaultColumnGrouping = createDefaultColumnGrouping();

    setColumns(defaultYearlyColumns);
    setColumnsMonth(defaultMonthlyColumns);
    setColumnGroup(defaultColumnGrouping);

    // ตั้งค่า filter options
    const baseOptions = [
      {
        nameEN: "Yearly",
        nameTH: "รายปี",
        value: "yearly",
      },
    ];

    if (selectedEcoEfficiency?.periodType !== "yearly") {
      baseOptions.push({
        nameEN: "Monthly",
        nameTH: "รายเดือน",
        value: "monthly",
      });
    }

    setFilterOption(baseOptions);
    setSelectedComponants("yearly");
  }, [t, selectedEcoEfficiency]);

  const getMonthLabel = (month: number) => {
    const months = [
      t("Jan"),
      t("Feb"),
      t("Mar"),
      t("Apr"),
      t("May"),
      t("Jun"),
      t("Jul"),
      t("Aug"),
      t("Sep"),
      t("Oct"),
      t("Nov"),
      t("Dec"),
    ];
    return months[month - 1] || "";
  };

  const buildTable = () => {
    // console.log('🔍 Building Yearly Table - yearly data:', yearly);
    // console.log('🔍 Building Yearly Table - indicators:', indicators);
    let temp: EcoEfficiencyPerformanceOverTimeDataGrid[];
    const createComponentProperties = (
      components: EconomicComponentYearly[],
    ) => {
      const componentProps: any = {};

      components.forEach((comp) => {
        // หา component ที่ตรงกับ economiccomponentsRef ใน indicators.components
        let matchedComponent: EconomicIndicatorComponent | undefined;

        indicators.forEach((indicator) => {
          const foundComponent = indicator.components.find(
            (component) => component._id === comp.economiccomponentsRef,
          );
          if (foundComponent) {
            matchedComponent = foundComponent;
          }
        });

        if (matchedComponent) {
          // เพิ่ม properties ใหม่โดยใช้ ref ของ component เป็น key
          componentProps[comp.economiccomponentsRef] = comp.componentValue;
        }
      });
      return componentProps;
    };

    if (yearly && yearly.years.length > 0) {
      temp = [
        ...yearly.years.map((y, index) => {
          const componentProps = createComponentProperties(y.components || []);
          return {
            _id: index,
            id: y.year,
            // economicValue: y.economicValue,
            // environmentalImactValue: y.environmentImpact,
            normalizationBaseYear: y.ecoEfficiencyBaseYearNormalized,
            normalizationPlan: y.ecoEfficiencyPlanNormalized,
            normalizationActual: y.ecoEfficiencyNormalized,
            ...componentProps,
          };
        }),
      ];
    } else {
      temp = Array.from({ length: 1 }, (_, index) => {
        const componentProps: any = {};
        indicators.forEach((indicator) => {
          indicator.components.forEach((component) => {
            const matchedYearlyComponent = yearly?.components.find(
              (c) => c.economiccomponentsRef === component._id,
            );
            componentProps[component._id] =
              matchedYearlyComponent?.componentValue ?? 0;
          });
        });
        return {
          _id: index,
          id: (selectedEcoEfficiency?.year ?? 0) + index,
          economicValue: index === 0 ? yearly?.economicValue ?? 0 : 0,
          environmentalImactValue:
            index === 0 ? yearly?.environmentImpact ?? 0 : 0,
          normalizationBaseYear:
            index === 0 ? yearly?.ecoEfficiencyBaseYearNormalized ?? 0 : 0,
          normalizationPlan:
            index === 0 ? yearly?.ecoEfficiencyPlanNormalized ?? 0 : 0,
          normalizationActual:
            index === 0 ? yearly?.ecoEfficiencyNormalized ?? 0 : 0,
          ...componentProps,
        };
      });
    }
    // console.log('🔍 Yearly Rows Data:', temp);
    setRows(temp);
  };

  const buildTableMonth = () => {
    // console.log('🔍 Building Monthly Table - monthly data:', monthly);
    // console.log('🔍 Monthly structure check:');
    // console.log('  - monthly.monthly:', monthly?.monthly);
    // console.log('  - monthly.monthly[0]:', monthly?.monthly?.[0]);
    // console.log('  - monthly.monthly[0]?.components:', monthly?.monthly?.[0]?.components);
    // console.log('🔍 Building Monthly Table - indicators:', indicators);
    let temp: EcoEfficiencyPerformanceOverTimeDataGrid[] = [];

    const createComponentProperties = (components: any[]) => {
      const componentProps: any = {};

      components.forEach((comp) => {
        let matchedComponent: EconomicIndicatorComponent | undefined;

        indicators.forEach((indicator) => {
          const foundComponent = indicator.components.find(
            (component) => component._id === comp.economiccomponentsRef,
          );
          if (foundComponent) {
            matchedComponent = foundComponent;
          }
        });
        if (matchedComponent) {
          componentProps[comp.economiccomponentsRef] = comp.componentValue;
        }
      });
      return componentProps;
    };

    if (monthly && monthly.monthly.length > 0) {
      temp = [
        ...monthly.monthly.map((m, index) => {
          const componentProps = createComponentProperties(m.components || []);
          return {
            _id: index,
            id: index + 1,
            month: getMonthLabel(m.month),
            // economicValue: m.economicValue,
            // environmentalImactValue: m.environmentImpact,
            normalizationBaseYear: m.ecoEfficiencyBaseYearNormalized,
            normalizationPlan: m.ecoEfficiencyPlanNormalized,
            normalizationActual: m.ecoEfficiencyNormalized,
            ...componentProps,
          };
        }),
        // เพิ่มแถว Total Rebuild when has object
        {
          _id: monthly.monthly.length,
          id: monthly.monthly.length + 1,
          month: t("Total"),
          // economicValue: monthly.economicValue,
          // environmentalImactValue: monthly.environmentImpact,
          normalizationBaseYear: monthly.ecoEfficiencyBaseYearNormalized,
          normalizationPlan: monthly.ecoEfficiencyPlanNormalized,
          normalizationActual: monthly.ecoEfficiencyNormalized,
          // Total row, รวมค่าจากทุกเดือน
          // componants รวมจากทุกเดือน
          // ...(() => {
          //   // console.log('Creating Total row components...');
          //   const totalComponents: any = {};

          //   monthly.monthly.forEach((monthData) => {
          //     // components ของแต่ละเดือน
          //     monthData.components?.forEach((comp: any) => {
          //       // ถ้ายังไม่มี key นี้ใน totalComponents ให้สร้างใหม่เป็น 0
          //       if (!totalComponents[comp.economiccomponentsRef]) {
          //         totalComponents[comp.economiccomponentsRef] = 0;
          //       }
          //       totalComponents[comp.economiccomponentsRef] += comp.componentValue || 0;
          //     });
          //   });

          //   // console.log('Total components (sum of all months):', totalComponents);
          //   return totalComponents;
          // })(),

          ...(() => {
            const totalComponents: any = {};
            yearly?.components?.forEach((comp: any) => {
              totalComponents[comp.economiccomponentsRef] =
                comp.componentValue || 0;
            });

            return totalComponents;
          })(),

          // Total row, รวมค่าจากทุกเดือน หรือใช้ค่าจาก monthly root level
          // ถ้าไม่มี componants ใน monthly root ให้ใช้ค่าจากเดือนสุดท้าย หรือรวมจากทุกเดือน
          // ...(() => {
          //   // console.log('🔍 Creating Total row components...');
          //   const lastMonthComponents =
          //     monthly.monthly.length > 0
          //       ? monthly.monthly[monthly.monthly.length - 1].components
          //       : [];
          //   // console.log('🔍 Last month components:', lastMonthComponents);
          //   return (
          //     lastMonthComponents?.reduce((acc: any, comp: any) => {
          //       acc[comp.economiccomponentsRef] = comp.componentValue;
          //       return acc;
          //     }, {}) || {}
          //   );
          // })(),
        },
      ];
    } else {
      temp = [];
    }
    // console.log('🔍 Monthly Rows Data:', temp);
    setRowsMonth(temp);
  };

  useEffect(() => {
    if (indicators.length > 0 && yearly) {
      const result = yearly;
      setData(result);

      if (result.years.length > 0) {
        // สร้างกราฟ 6 ปีเสมอ นับจากปีที่เลือก
        const startYear = selectedEcoEfficiency?.year ?? result.years[0].year;
        const sixYearLabels = Array.from({ length: 6 }, (_, i) =>
          String(startYear + i),
        );

        // สร้าง data สำหรับ Plan โดยจับคู่กับข้อมูลที่มี
        const planData = Array.from({ length: 6 }, (_, i) => {
          const targetYear = startYear + i;
          const yearData = result.years?.find((y) => y.year === targetYear);
          return yearData ? yearData.ecoEfficiencyPlanNormalized || 0 : 0;
        });

        // สร้าง data สำหรับ Actual โดยจับคู่กับข้อมูลที่มี
        const actualData = Array.from({ length: 6 }, (_, i) => {
          const targetYear = startYear + i;
          const yearData = result.years?.find((y) => y.year === targetYear);
          return yearData ? yearData.ecoEfficiencyNormalized : 0;
        });

        setSeries([
          // {
          //   data: planData,
          //   color: "#10B981",
          //   label: `${t("Plan")}`,
          // },
          {
            data: actualData,
            color: "#2563EB",
            label: `${t("Actual")}`,
          },
        ]);

        setLabels(sixYearLabels);
      } else {
        setSeries([
          // {
          //   data: [result.ecoEfficiencyPlanNormalized, ...Array(5).fill(0)],
          //   color: "#0B9485",
          //   label: `${t("Plan")}`,
          // },
          {
            data: [result.ecoEfficiencyNormalized, ...Array(5).fill(0)],
            color: "#2563EB",
            label: `${t("Actual")}`,
          },
        ]);

        setLabels(
          [0, 1, 2, 3, 4, 5].map((y) => `${selectedEcoEfficiency!.year + y}`),
        );
      }
    }

    if (indicators.length > 0 && monthly) {
      const result = monthly;
      setDataMonth(monthly);
      if (result.monthly.length > 0) {
        setSeriesMonth([
          // {
          //   data: [
          //     ...result.monthly?.map((y) => y.ecoEfficiencyPlanNormalized),
          //     result.ecoEfficiencyPlanNormalized,
          //   ],
          //   color: "#10B981",
          //   label: `${t("Plan")}`,
          // },
          {
            data: [
              ...result.monthly?.map((y) => y.ecoEfficiencyNormalized),
              result.ecoEfficiencyNormalized,
            ],
            color: "#2563EB",
            label: `${t("Actual")}`,
          },
        ]);

        setLabelsMonth([
          ...result.monthly?.map((y) => getMonthLabel(y.month) || ""),
          // t("Total"),
        ]);
      }
    }
  }, [yearly, monthly, indicators, t]);

  useEffect(() => {
    if (yearly) {
      setColumnGroup([
        {
          groupId: t("Components"),
          children: [
            ...yearly!.components.map((c) => {
              const indicator = indicators.find((i) =>
                i.components.find((j) => j._id === c.economiccomponentsRef),
              );

              const component = indicator?.components.find(
                (i) => i._id === c.economiccomponentsRef,
              );
              return {
                field: `${component?._id}`,
              };
            }),
          ],
          headerAlign: "center",
        },
        {
          groupId: t("Normalized-Eco-Efficiency"),
          children: [
            { field: `normalizationBaseYear` },
            { field: `normalizationPlan` },
            { field: `normalizationActual` },
          ],
          headerAlign: "center",
        },
      ]);
      setColumns([
        {
          field: "id",
          headerName: t("No."),
          width: 80,
          headerAlign: "center",
          align: "center",
          sortable: false,
          filterable: false,
          renderCell: (params) => params.row._id + 1,
        },
        {
          field: "year",
          headerName: t("Year"),
          minWidth: 120,
          sortable: false,
          filterable: false,
          renderCell: (params) => params.row.id,
        },
        ...(yearly!.components || []).map((c, index): GridColDef => {
          const indicator = indicators.find((i) =>
            i.components.find((j) => j._id === c.economiccomponentsRef),
          );

          const component = indicator?.components.find(
            (i) => i._id === c.economiccomponentsRef,
          );
          return {
            field: `${c.economiccomponentsRef}`,
            headerName: `${
              i18n.language === "th"
                ? component?.nameTh ?? ""
                : component?.nameEn ?? ""
            }`,
            minWidth: 160,
            sortable: false,
            filterable: false,
            headerAlign: "center",
            align: "right",
            flex: 1,
            valueGetter: (value: any, row: any) => {
              return (
                NumberFormatterUtils.numberFormat(
                  row[c.economiccomponentsRef],
                ) ?? 0
              );
            },
          };
        }),
        // {
        //   field: "economicValue",
        //   headerName: t("Economic-Value"),
        //   minWidth: 150,
        //   headerAlign: "center",
        //   align: "right",
        //   sortable: false,
        //   filterable: false,
        //   renderCell: (params) =>
        //     NumberFormatterUtils.numberFormat(params.row.economicValue) ?? 0,
        // },
        // {
        //   field: "environmentImpactValue",
        //   headerName: t("Environment-Impact-Value"),
        //   minWidth: 150,
        //   headerAlign: "center",
        //   align: "right",
        //   sortable: false,
        //   filterable: false,
        //   renderCell: (params) =>
        //     NumberFormatterUtils.numberFormat(
        //       params.row.environmentalImactValue
        //     ) ?? 0,
        // },
        {
          field: "normalizationBaseYear",
          headerName: t("Base-Year"),
          headerAlign: "right",
          align: "right",
          minWidth: 120,
          sortable: false,
          filterable: false,
          valueFormatter: (value) =>
            NumberFormatterUtils.numberFormat(value, 4) ?? 0,
        },
        // {
        //   field: "normalizationPlan",
        //   headerName: t("Plan"),
        //   headerAlign: "right",
        //   align: "right",
        //   minWidth: 120,
        //   sortable: false,
        //   filterable: false,
        //   valueFormatter: (value) =>
        //     NumberFormatterUtils.numberFormat(value, 4) ?? 0,
        // },
        {
          field: "normalizationActual",
          headerName: t("Actual"),
          headerAlign: "right",
          align: "right",
          minWidth: 120,
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            return (
              <Tooltip title={params.formattedValue}>
                <Box color={"var( --status-waiting-leased-in-text)"}>
                  {NumberFormatterUtils.numberFormat(params.formattedValue, 4)}
                </Box>
              </Tooltip>
            );
          },
        },
      ]);
      buildTable();
    }

    if (monthly) {
      setColumnsMonth([
        {
          field: "id",
          headerName: t("No."),
          width: 80,
          headerAlign: "center",
          align: "center",
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            if (params.row.month === t("Total")) {
              return "";
            }
            return params.row._id + 1;
          },
        },
        {
          field: "month",
          headerName: t("Month"),
          minWidth: 120,
          sortable: false,
          filterable: false,
          renderCell: (params) => params.row.month,
        },
        ...(monthly!.monthly[0]?.components || []).map(
          (c, index): GridColDef => {
            const indicator = indicators.find((i) =>
              i.components.find((j) => j._id === c.economiccomponentsRef),
            );

            const component = indicator?.components.find(
              (i) => i._id === c.economiccomponentsRef,
            );
            return {
              field: `${c.economiccomponentsRef}`,
              headerName: `${
                i18n.language === "th"
                  ? component?.nameTh ?? ""
                  : component?.nameEn ?? ""
              }`,
              minWidth: 140,
              sortable: false,
              filterable: false,
              flex: 1,
              headerAlign: "right",
              align: "right",
              valueGetter: (value: any, row: any) => {
                return (
                  NumberFormatterUtils.numberFormat(
                    row[c.economiccomponentsRef],
                  ) ?? 0
                );
              },
            };
          },
        ),
        // {
        //   field: "economicValue",
        //   headerName: t("Economic-Value"),
        //   minWidth: 150,
        //   headerAlign: "center",
        //   align: "right",
        //   sortable: false,
        //   filterable: false,
        //   renderCell: (params) =>
        //     NumberFormatterUtils.numberFormat(params.row.economicValue) ?? 0,
        // },
        // {
        //   field: "environmentImpactValue",
        //   headerName: t("Environment-Impact-Value"),
        //   minWidth: 150,
        //   headerAlign: "center",
        //   align: "right",
        //   sortable: false,
        //   filterable: false,
        //   renderCell: (params) =>
        //     NumberFormatterUtils.numberFormat(
        //       params.row.environmentalImactValue
        //     ) ?? 0,
        // },
        {
          field: "normalizationBaseYear",
          headerName: t("Base-Year"),
          headerAlign: "right",
          align: "right",
          minWidth: 120,
          sortable: false,
          filterable: false,
          valueFormatter: (value, row) => {
            return row.id === 13
              ? ""
              : NumberFormatterUtils.numberFormat(value, 4) ?? 0;
          },
        },
        // {
        //   field: "normalizationPlan",
        //   headerName: t("Plan"),
        //   headerAlign: "right",
        //   align: "right",
        //   minWidth: 120,
        //   sortable: false,
        //   filterable: false,
        //   valueFormatter: (value, row) => {
        //     return row.id === 13
        //       ? ""
        //       : NumberFormatterUtils.numberFormat(value, 4) ?? 0;
        //   },
        // },
        {
          field: "normalizationActual",
          headerName: t("Actual"),
          headerAlign: "right",
          align: "right",
          minWidth: 120,
          sortable: false,
          filterable: false,
          valueFormatter: (value, row) => {
            return row.id === 13
              ? ""
              : NumberFormatterUtils.numberFormat(value, 4) ?? 0;
          },
          renderCell: (params) => {
            return (
              <Tooltip title={params.formattedValue}>
                <Box color={"var( --status-waiting-leased-in-text)"}>
                  {params.formattedValue}
                </Box>
              </Tooltip>
            );
          },
        },
      ]);
      buildTableMonth();
    }
  }, [
    labels,
    series,
    data,
    monthly,
    yearly,
    indicators,
    t,
    selectedEcoEfficiency,
  ]);

  const getCurrentSeriesAndLabels = () => {
    // console.log('🔍 Current selected view:', selectedComponants);
    if (selectedComponants === "monthly") {
      // console.log('🔍 Using Monthly Data - rowsMonth:', rowsMonth);
      return {
        currentSeries: seriesMonth,
        currentLabels: labelsMonth,
        currentColumns: columnsMonth,
        currentRows: rowsMonth,
      };
    } else {
      // console.log('🔍 Using Yearly Data - rows:', rows);
      return {
        currentSeries: series,
        currentLabels: labels,
        currentColumns: columns,
        currentRows: rows,
      };
    }
  };

  const { currentSeries, currentLabels, currentColumns, currentRows } =
    getCurrentSeriesAndLabels();

  return (
    <>
      <CustomEcoEfficiencyChart
        series={currentSeries ?? []}
        labels={currentLabels ?? []}
        header={t("Eco-Efficiency-Performance-Over-Time")}
        info={t("Eco-Efficiency-Performance-Over-Time-Description")}
        year={2024}
        rows={currentRows}
        columns={currentColumns}
        columnGroupingModel={columnGroup ?? undefined}
        selectOption={filterOption}
        select={selectedComponants}
        setSelect={(value) => setSelectedComponants(value)}
      />
    </>
  );
};

export default EcoEfficiencyPerformanceOverTime;
